import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next"
import { addComplaint, getAllComplaints, updateComplaint } from "../../services/AppService";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import PageFade from "../../components/Login/PageFade";
import ComplaintFormModal from "./ComplaintFormModal";
import { useNavigate } from "react-router-dom";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import closeButton from "../../assets/images/close.png";
import ViewComplaintDetailsModal from "./ViewComplaintDetailsModal";

const Complaints=()=>{
    const {t}=useTranslation();
    const navigate=useNavigate();

    const [complaints,setComplaints]=useState([]);
    const [loading,setLoading]=useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingComplaint, setEditingComplaint] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
const [selectedComplaint, setSelectedComplaint] = useState(null);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);

   const [confirmModal, setConfirmModal] = useState({ open: false, formData: null });
    const [messageModal, setMessageModal] = useState({ open: false, type: "success", title: "", message: "" });

    const showSuccess = (msg) => setMessageModal({ open: true, type: "success", title: "Success", message: msg });
    const showError = (msg) => setMessageModal({ open: true, type: "error", title: "Error", message: msg });

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    const fetchComplaints=useCallback(async()=>{
        try{
            setLoading(true);
            const data=await getAllComplaints({search, page, size});
            setComplaints(data.content || []);
            setTotalPages(data.totalPages);
        }catch(error){
            console.error("Failed to fetch complaints", error);
           showError("Failed to load complaints");
        }finally{
            setLoading(false);
        }
    }, [page, size, debouncedSearch]);

    useEffect(()=>{fetchComplaints();}, [fetchComplaints]);


   const handleFormSubmit = (formData) => {
        setIsModalOpen(false);
        setConfirmModal({ open: true, formData: formData });
    };

    const handleConfirmSave = async () => {
        const { formData } = confirmModal;
        try {
            if (editingComplaint){
                await updateComplaint(editingComplaint.id, formData);
                showSuccess("Complaint updated successfully");
            } else {
                await addComplaint(formData);
                showSuccess("Complaint registered successfully");
            }
            fetchComplaints();
            setConfirmModal({ open: false, formData: null });
            setEditingComplaint(null);
        } catch (error) {
            showError(error.response?.data?.message || "Internal Server Error");
        }
    };

    const handleViewClick = (complaint) => {
    setSelectedComplaint(complaint);
    setIsViewModalOpen(true);
};

    return(
     <PageFade>
            <div className="bg-white shadow-lg rounded-2xl p-6">
                {/* Header Section */}
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h4 className="text-xl font-semibold text-blue">{t('pageTitles.complaintsList')}</h4>
                    <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
                </div>

                {/* Add New Button */}
                <div className="text-end mb-2">
                    <button className="btnAdd" onClick={() => { setEditingComplaint(null); setIsModalOpen(true); }}>
                        {t('common.addNew')}
                    </button>
                </div>

                {/* Entries and Search Bar */}
                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                    <div className="flex items-center gap-2">
                        <label className="text-sm">{t('common.show')}</label>
                        <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }} className="border rounded px-2 py-1 text-sm">
                            {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <label className="text-sm">{t('common.entries')}</label>
                    </div>
                    <input
                        type="text"
                        placeholder={t('common.search')}
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        className="border rounded px-3 py-1 text-sm outline-none focus:border-blue-400"
                    />
                </div>

                {/* Data Grid */}
                <div className="overflow-x-auto">
                    <table className="w-full border text-sm ctable rounded-lg">
                        <thead>
                            <tr className="bg-[#a0adf1] text-white">
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.srNo')}</th>
                                <th className="px-3 py-2 border-b border-black text-left">{t('complaint.complainerName')}</th>
                                <th className="px-3 py-2 border-b border-black text-left">{t('complaint.complainAgainst')}</th>
                                <th className="px-3 py-2 border-b border-black text-left">{t('complaint.department')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.view')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.edit')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="text-center py-4">{t('common.loading')}</td></tr>
                            ) : complaints.length === 0 ? (
                                <tr><td colSpan={6} className="text-center py-4">{t('common.noRecords')}</td></tr>
                            ) : (
                                complaints.map((item, idx) => (
                                    <tr key={item.id} className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100 transition-colors`}>
                                        <td className="px-3 py-2 text-center">{page * size + idx + 1}</td>
                                        <td className="px-3 py-2 font-medium">{item.complainerName}</td>
                                        <td className="px-3 py-2 text-slate-600">{item.complainAgainst}</td>
                                        <td className="px-3 py-2 text-slate-600">{item.departmentName}</td>
                                        <td className="px-3 py-2 text-center">
                                            <button className="px-3 btnImgView py-1 rounded text-xs" onClick={() => handleViewClick(item)}></button>
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <button 
                                                className="px-3 py-1 btnImgEdit rounded text-xs" 
                                                onClick={() => { setEditingComplaint(item); setIsModalOpen(true); }}
                                            ></button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div className="flex justify-between items-center mt-4 text-sm">
                    <p>
                        {t('common.showing')} {page * size + 1} {t('common.to')} {page * size + complaints.length} {t('common.of')} {totalPages * size} {t('common.entries')}
                    </p>
                    <div className="flex gap-2">
                        <button 
                            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 transition-all" 
                            onClick={() => setPage(p => Math.max(p - 1, 0))} 
                            disabled={page === 0}
                        >
                            {t('common.previous')}
                        </button>
                        <button 
                            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 transition-all" 
                            onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} 
                            disabled={page >= totalPages - 1}
                        >
                            {t('common.next')}
                        </button>
                    </div>
                </div>

                {/* Modals */}
                <ComplaintFormModal
                    isOpen={isModalOpen}
                    editData={editingComplaint}
                    onSubmit={handleFormSubmit}
                    onClose={() => { setIsModalOpen(false); setEditingComplaint(null); }}
                />

                <ConfirmActionModal
                    open={confirmModal.open}
                    title={editingComplaint ? "Confirm Update" : "Confirm Addition"}
                    message={`Are you sure you want to ${editingComplaint ? "update" : "save"} this complaint detail?`}
                    onConfirm={handleConfirmSave}
                    onClose={() => setConfirmModal({ open: false, formData: null })}
                />

                {/* view modal for complaints */}

                <ViewComplaintDetailsModal
                    isOpen={isViewModalOpen}
                    onClose={() => setIsViewModalOpen(false)}
                    complaint={selectedComplaint}
                />
                

                <MessageModal
                    open={messageModal.open}
                    onClose={() => setMessageModal({ ...messageModal, open: false })}
                    {...messageModal}
                />
            </div>
        </PageFade>
    )
}

export default Complaints;